/* eslint-disable max-len */
import { Box, Button, useTheme } from "@mui/material";
import { BoxContent, bookAVisitMobile, mainContentStyle, sidebarStyle } from "./LayoutApp.style";
import { CalendarPlusIcon, HomeIconNew, MyCare, ReferFriendIcon } from "../Icons";
import { ContentLayout, DrawerGeneric, Wrapper } from "..";
import { GET_ALL_NOTIFICATIONS, NOTIFICATIONS } from "../../services/notifications/notifications-query";
import {
  CompletePurchaseCheckoutContainer,
  ManagePlanContainer,
  PetCareContainer,
  PetEditProfileContainer,
  PrequalificationContainer,
  PurchaseContinuationContainer,
  ReferAFriendContainer,
  StoreContainer,
  WellnessPlanRoutingContainer,
} from "../../shared/containers";
import { Navigate, Route, Routes } from "react-router-dom";
import React, { useEffect, useRef } from "react";

import AppHeaderMobile from "../AppHeaderMobile/AppHeaderMobile";
import Dashboard from "../../pages/Dashboard/Dashboard";
import HomeActiveIcon from "../Icons/HomeActiveIcon";
import { IRootState } from "../../state";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { MODALS } from "../../config/params";
import MyAccountActiveIcon from "../Icons/MyAccountActiveIcon";
import MyAccountIcon from "../Icons/MyAccountIcon";
import MyCareActiveIcon from "../Icons/MyCareActiveIcon";
import NavBar from "../NavBar/NavBar";
import { PAGES_PATH } from "../../navigation/params";
import UserRoutingContainer from "../../shared/containers/UserRoutingContainer/UserRoutingContainer";
import { useApolloClient } from "@apollo/client";
import { useGTM } from "../../hooks/useGTM";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useModal } from "../../context/ModalContext";
import { useSelector } from "react-redux";
import { userActivityLogger } from "../../utils/mixpanel";
import { PrivacyPolicy } from "../../pages/PrivacyPolicy";
import { TermsAndConditions } from "../../pages/TermsAndConditions";
import { VersionsView } from "../../pages/Versions";
import { HealthView } from "../../pages/HealthView";
import { useOverdueInvoiceStore } from "../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { EXPRESS_NOTIFICATIONS } from "../../services/express-notifications/express-notifications-query";
import { useExpressNotificationsStore } from "../../hooks/redux-hooks/express-notifications-store/express-notifications-store";
import { IItem } from "../DrawerGeneric/DrawerGeneric";
import StoreIcon from "../Icons/StoreIcon";
import StoreActiveIcon from "../Icons/StoreActiveIcon";

export const MENU_ITEMS: IItem[] = [
  {
    label: "Home",
    icon: <HomeIconNew />,
    to: PAGES_PATH.DASHBOARD,
    activeIcon: <HomeActiveIcon />,
  },
  {
    label: "Care",
    icon: <MyCare />,
    to: PAGES_PATH.PET_CARE,
    activeIcon: <MyCareActiveIcon />,
  },
  {
    label: "Pharmacy",
    icon: <StoreIcon />,
    to: PAGES_PATH.STORE,
    activeIcon: <StoreActiveIcon />,
  },
  {
    label: "Refer a Friend",
    icon: <ReferFriendIcon color="gray" />,
    to: `${PAGES_PATH.REFER_FRIEND}?source=left_nav`,
    activeIcon: <ReferFriendIcon />,
  },
  {
    label: "Account",
    icon: <MyAccountIcon />,
    to: PAGES_PATH.USER_PROFILE,
    activeIcon: <MyAccountActiveIcon />,
  },
];

const LayoutApp = () => {
  const client = useApolloClient();
  const { user } = useSelector((state: IRootState) => state.user);
  const { setExpressNotificationList } = useExpressNotificationsStore();
  const subscriptions = useRef<any[]>([]);

  useEffect(() => {
    if (user?.objectUserId) {
      subscriptions.current.forEach((sub) => sub.unsubscribe());
      subscriptions.current = [];

      const notificationSub = client
        .subscribe({
          query: NOTIFICATIONS,
          variables: { userId: user.objectUserId },
        })
        .subscribe({
          next({ data }) {
            client.refetchQueries({ include: [GET_ALL_NOTIFICATIONS] });
          },
          error(error) {
            console.error("Notification subscription error:", error);
          },
        });

      const expressNotificationSub = client
        .subscribe({
          query: EXPRESS_NOTIFICATIONS,
          variables: { userId: user.objectUserId },
        })
        .subscribe({
          next({ data }) {
            setExpressNotificationList(data?.expressNotification?.notifications);
          },
          error(error) {
            console.error("Express notification subscription error:", error);
          },
        });

      subscriptions.current.push(notificationSub, expressNotificationSub);
    }

    return () => {
      subscriptions.current.forEach((sub) => sub.unsubscribe());
      subscriptions.current = [];
    };
  }, [user, client]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileAndTab = useMediaQuery(theme.breakpoints.down("md"));
  const isOpenSidebar = useMediaQuery(theme.breakpoints.up(1100));
  const { dispatchEvent } = useGTM();
  const { openModal } = useModal();
  const { pendingPaymentPetList, isBookingBlocked, pausedWellnessPetList, cancelledWellnessPetList } =
    useOverdueInvoiceStore();

  const handleGoToBookAVisit = () => {
    userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT, {
      location: user?.location?.locationName,
      msa: user?.location?.city,
      start_from: "left_nav",
      appt_type: "in_room_visit",
      overdue_status: pendingPaymentPetList.length > 0 ? "yes" : "no",
      is_plan_cancelled: cancelledWellnessPetList.length > 0 ? "true" : "false",
    });
    if (!isBookingBlocked && pendingPaymentPetList.length > 0) {
      if (cancelledWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
      } else if (pausedWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL);
      } else {
        openModal(MODALS.GENERIC_PAYMENT_ISSUE_MODAL);
      }
    } else {
      openModal(MODALS.UPDATED_BOOK_A_VISIT_MODAL);
    }
    dispatchEvent({
      eventName: "gv_book_a_visit_button_sidebar",
    });
  };

  return (
    <div>
      {isMobileAndTab && <AppHeaderMobile isTop={isMobile ? true : false} isMobile={isMobile} />}
      {isMobile && <NavBar />}
      <BoxContent>
        <Box component="main" sx={sidebarStyle}>
          <DrawerGeneric open={isOpenSidebar} itemsMenu={MENU_ITEMS} user={user} />
        </Box>
        <Box component="main" sx={mainContentStyle}>
          <Box className={`h-full ${isMobile || isMobileAndTab ? "mt-2" : ""} flex`}>
            <ContentLayout>
              <Routes>
                <Route path={PAGES_PATH.PRIVACY_POLICY} element={<PrivacyPolicy />} />
                <Route path={PAGES_PATH.HEALTH} element={<HealthView />} />
                <Route path={PAGES_PATH.TERMS_CONDITIONS} element={<TermsAndConditions />} />
                <Route path={PAGES_PATH.VERSIONS} element={<VersionsView />} />

                <Route path={`${PAGES_PATH.WELLNESS_PLAN_DETAILS}/*`} element={<WellnessPlanRoutingContainer />} />
                <Route path={PAGES_PATH.PREQUALIFICATION} element={<PrequalificationContainer />} />
                <Route path={PAGES_PATH.COMPLETE_PURCHASE} element={<PurchaseContinuationContainer />} />
                <Route path={PAGES_PATH.COMPLETE_PURCHASE_CHECKOUT} element={<CompletePurchaseCheckoutContainer />} />
                <Route path={PAGES_PATH.MANAGE_PLAN} element={<ManagePlanContainer />} />
                <Route path={PAGES_PATH.DASHBOARD} element={<Dashboard />} />
                <Route path={PAGES_PATH.PET_CARE} element={<PetCareContainer />} />
                <Route path={PAGES_PATH.STORE} element={<StoreContainer />} />
                <Route path={`${PAGES_PATH.USER_PROFILE}/*`} element={<UserRoutingContainer />} />
                <Route path={PAGES_PATH.PET_PROFILE} element={<PetEditProfileContainer />} />
                <Route path={"*"} element={<Navigate to={PAGES_PATH.DASHBOARD} />} />
                <Route path={PAGES_PATH.REFER_FRIEND} element={<ReferAFriendContainer />} />
              </Routes>
            </ContentLayout>
            <Wrapper />
          </Box>
        </Box>
        {isMobile && (
          <Button
            id="bookVisitButtonFloating"
            color="primary"
            size="small"
            sx={bookAVisitMobile}
            onClick={handleGoToBookAVisit}
          >
            <CalendarPlusIcon />
          </Button>
        )}
      </BoxContent>
    </div>
  );
};

export default LayoutApp;
